import React from "react";
import { useTranslation } from "react-i18next";

import { Box, InputAdornment, Stack, TextField } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { getEndOfPeriod } from "utils/debt";
import toIsoMidday from "utils/to-iso-midday";

import { FloatTypeSelect } from "../../form-fields/FloatTypeSelect";
import { RateFixingTypeSelect } from "../../form-fields/RateFixingTypeSelect";
import { ReferenceIndexSelect } from "../../form-fields/ReferenceIndexSelect";
import { FormValues } from "../../schemas";
import { DAY_COUNT_CONVENTION_OPTIONS, getFrequencyOptions } from "../constants";
import { QuestionSlot } from "../QuestionSlot";
import { IndexField } from "../types";

interface Step5Props {
  formik: FormikProps<FormValues>;
  dateFormat: string;
  isSimpleLoan: boolean;
  hasRegularInterest: boolean;
  revealedFields: Set<string>;
  reveal: (name: string) => void;
  fetchIndexValue: (index: string, field: IndexField) => Promise<void>;
}

export const Step5PIKTerms: React.FC<Step5Props> = ({
  formik,
  dateFormat,
  isSimpleLoan,
  hasRegularInterest,
  revealedFields,
  reveal,
  fetchIndexValue
}) => {
  const { t } = useTranslation("debt");
  const { values, setFieldValue } = formik;

  return (
    <Box>
      <QuestionSlot visible label={t("wizard.pikTerms.baseRateQuestion")}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            type="number"
            label={t("wizard.pikTerms.baseRateLabel")}
            value={values.pik_base_rate ?? ""}
            onChange={(e) => {
              setFieldValue("pik_base_rate", e.target.value === "" ? null : parseFloat(e.target.value));
              reveal("pik_base_rate");
            }}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
          />
          <FloatTypeSelect
            name="pik_float_type"
            formik={formik}
            label={t("wizard.pikTerms.rateStructureLabel")}
            onChange={(e) => {
              formik.handleChange(e);
              if (e.target.value === "fixed") {
                setFieldValue("pik_reference_index", null);
                setFieldValue("pik_index_value", null);
              }
              if (e.target.value !== "linked") {
                setFieldValue("pik_link_percent", 0);
              }
              if (e.target.value !== "spread") {
                setFieldValue("pik_rate_fixing", null);
              }
              reveal("pik_float_type");
            }}
          />
        </Stack>
      </QuestionSlot>

      <QuestionSlot visible={values.pik_float_type !== "fixed"} label={t("wizard.pikTerms.indexQuestion")}>
        <ReferenceIndexSelect
          name="pik_reference_index"
          formik={formik}
          label={t("wizard.pikTerms.indexLabel")}
          onIndexChange={(idx) => fetchIndexValue(idx, "pik_index_value")}
        />
      </QuestionSlot>

      <QuestionSlot visible={values.pik_float_type === "spread"} label={t("wizard.pikTerms.rateFixingQuestion")}>
        <RateFixingTypeSelect name="pik_rate_fixing" formik={formik} label={t("wizard.pikTerms.rateFixingLabel")} />
      </QuestionSlot>

      <QuestionSlot visible={values.pik_float_type === "linked"} label={t("wizard.pikTerms.linkPercentQuestion")}>
        <TextField
          fullWidth
          type="number"
          label={t("wizard.pikTerms.linkPercentLabel")}
          value={values.pik_link_percent ?? ""}
          onChange={(e) => setFieldValue("pik_link_percent", e.target.value === "" ? null : parseFloat(e.target.value))}
          slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
        />
      </QuestionSlot>

      {isSimpleLoan && (
        <QuestionSlot
          visible={!!values.pik_capitalization_frequency && values.pik_float_type === "linked"}
          label={t("wizard.pikTerms.refDateQuestion")}
        >
          <Stack spacing={3}>
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              label={t("wizard.pikTerms.refDateLabel")}
              value={values.pik_reference_day ? dayjs(values.pik_reference_day) : null}
              onChange={(v) => setFieldValue("pik_reference_day", v?.isValid() ? toIsoMidday(v) : null)}
              sx={{ width: "100%" }}
            />
          </Stack>
        </QuestionSlot>
      )}

      {!hasRegularInterest && (
        <QuestionSlot
          visible={values.pik_float_type === "spread" && values.pik_rate_fixing === "in_advance"}
          label={t("wizard.interestTerms.loanResetFrequencyQuestion")}
        >
          <Stack spacing={3}>
            <Select
              name="loan_reset_frequency"
              label={t("wizard.interestTerms.loanResetFrequencyLabel")}
              variant="outlined"
              fullWidth
              value={values.loan_reset_frequency ?? ""}
              items={[{ key: "never", value: "", text: t("wizard.common.never") }, ...getFrequencyOptions(t)]}
              onChange={(e) => {
                const freq = e.target.value || null;
                setFieldValue("loan_reset_frequency", freq);
                if (isSimpleLoan && freq && values.start_date)
                  setFieldValue("loan_reset_date", getEndOfPeriod(dayjs(values.start_date), freq));
              }}
            />
            <TextField
              fullWidth
              type="number"
              name="loan_reset_date_offset"
              label={t("wizard.common.resetDateOffset")}
              value={values.loan_reset_date_offset ?? ""}
              onChange={(e) =>
                setFieldValue("loan_reset_date_offset", e.target.value === "" ? null : parseInt(e.target.value, 10))
              }
              slotProps={{ input: { inputProps: { step: 1 } } }}
            />
          </Stack>
        </QuestionSlot>
      )}

      {!hasRegularInterest && (
        <QuestionSlot
          visible={isSimpleLoan && values.pik_float_type === "spread" && values.pik_rate_fixing === "in_advance"}
          label={t("wizard.interestTerms.loanResetDateQuestion")}
        >
          <Stack spacing={3}>
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              label={t("wizard.interestTerms.loanResetDateLabel")}
              value={values.loan_reset_date ? dayjs(values.loan_reset_date) : null}
              onChange={(v) => setFieldValue("loan_reset_date", v?.isValid() ? toIsoMidday(v) : null)}
              sx={{ width: "100%" }}
              slotProps={{ textField: { helperText: t("wizard.common.autoCalculated") } }}
            />
          </Stack>
        </QuestionSlot>
      )}

      <QuestionSlot visible={revealedFields.has("pik_base_rate")} label={t("wizard.pikTerms.capFrequencyQuestion")}>
        <Stack spacing={3}>
          <Select
            name="pik_capitalization_frequency"
            label={t("wizard.pikTerms.capFrequencyLabel")}
            variant="outlined"
            fullWidth
            value={values.pik_capitalization_frequency ?? ""}
            items={[{ key: "none", value: "", text: t("wizard.common.none") }, ...getFrequencyOptions(t)]}
            onChange={(e) => {
              const freq = e.target.value || null;
              setFieldValue("pik_capitalization_frequency", freq);
              if (isSimpleLoan && freq && values.start_date)
                setFieldValue("pik_first_capitalization_date", getEndOfPeriod(dayjs(values.start_date), freq));
              reveal("pik_cap_frequency");
            }}
          />
          {isSimpleLoan && values.pik_capitalization_frequency && (
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              label={t("wizard.pikTerms.firstCapLabel")}
              value={values.pik_first_capitalization_date ? dayjs(values.pik_first_capitalization_date) : null}
              onChange={(v) => setFieldValue("pik_first_capitalization_date", v?.isValid() ? toIsoMidday(v) : null)}
              sx={{ width: "100%" }}
              slotProps={{ textField: { helperText: t("wizard.common.autoCalculated") } }}
            />
          )}
        </Stack>
      </QuestionSlot>

      {!hasRegularInterest && (
        <QuestionSlot
          visible={revealedFields.has("pik_cap_frequency")}
          label={t("wizard.interestTerms.dayCountQuestion")}
        >
          <Select
            name="day_count_convention"
            label={t("wizard.common.dayCountConvention")}
            variant="outlined"
            fullWidth
            value={values.day_count_convention}
            items={DAY_COUNT_CONVENTION_OPTIONS}
            onChange={formik.handleChange}
          />
        </QuestionSlot>
      )}

      <QuestionSlot visible={revealedFields.has("pik_cap_frequency")} label={t("wizard.pikTerms.floorCeilingQuestion")}>
        <Stack direction="row" spacing={3}>
          <TextField
            fullWidth
            type="number"
            label={t("wizard.pikTerms.floorRateLabel")}
            value={values.pik_floor_rate ?? ""}
            onChange={(e) => setFieldValue("pik_floor_rate", e.target.value === "" ? null : parseFloat(e.target.value))}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
          />
          <TextField
            fullWidth
            type="number"
            label={t("wizard.pikTerms.ceilingRateLabel")}
            value={values.pik_ceiling_rate ?? ""}
            onChange={(e) =>
              setFieldValue("pik_ceiling_rate", e.target.value === "" ? null : parseFloat(e.target.value))
            }
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
          />
        </Stack>
      </QuestionSlot>
    </Box>
  );
};
